import { FC } from 'react';
import { useDesktop } from '@utils/hooks/useDesktop';
import { useAgentChatGate } from '@hooks/useAgentChatGate';
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { AGENT_CHAT_FEATURE_KEY } from '@utils/featureRollout/constants';
import { PaulinaInvite } from './PaulinaInvite';
import { usePaulinaShouldYield } from './usePaulinaShouldYield';

/**
 * PaulinaInviteHost — host de la INVITACIÓN al rollout de Paulina ("Conoce a Paulina, pruébala").
 *
 * IMPORTANTE: vive FUERA de PaulinaGate/PaulinaProvider. En la variante `control` (aún no aceptó)
 * el provider NO se monta (PaulinaGate solo monta con offered=true), así que la invitación NO
 * puede vivir dentro del AgentChatWidget — debe renderizarse aquí, a nivel Layout (dentro de
 * NotificationProvider, para que usePaulinaShouldYield tenga las notificaciones). Mismo reparto
 * que la feature de referencia (BLE: el coachmark de invitación vive en la página, independiente
 * de offered; el revert/feedback viven con la feature montada).
 *
 * Cede ante pedidos entrantes (no se monta si hay pedido) igual que el nudge. Al aceptar, el gate
 * cambia la variante a treatment → offered pasa a true → PaulinaGate monta el provider y Paulina
 * aparece. (No abre el chat automáticamente: el provider aún no existe al hacer accept; el comercio
 * abre Paulina con la pill/avatar — la invitación ya activó la feature. Ver reporte.)
 */

export const PaulinaInviteHost: FC = () => {
  const isDesktop = useDesktop();
  const gate = useAgentChatGate();
  // `inviteSurface` ('card' | 'modal' | 'none') lo gobierna el backend y NO lo expone el contrato
  // FeatureGate (solo invitación booleana) → se lee del feature-version directamente, como en menú.
  const { inviteSurface } = useFeatureVersion(AGENT_CHAT_FEATURE_KEY);
  const { hayPedido } = usePaulinaShouldYield();

  // Cede ante pedidos (lo operativo gana). showInvite ya implica control + experimento + cadencia.
  if (!gate.showInvite || hayPedido) return null;

  return (
    <PaulinaInvite
      surface={inviteSurface}
      anchor={isDesktop ? 'desktop' : 'mobile'}
      onAccept={gate.accept}
      onDismiss={gate.dismiss}
      onShown={gate.shown}
    />
  );
};
